import React from 'react';
import { Button, Icon } from '@repo/ui';
import { ConditionGroupBuilder } from './ConditionGroupBuilder';

interface ConditionRule {
  id: string;
  fieldId: number;
  fieldName: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'in' | 'not_in' | 'greater_than' | 'less_than';
  value: string | string[] | null;
}

interface ConditionGroup {
  id: string;
  conditions: ConditionRule[];
}

interface CustomFieldOption {
  id: number;
  name: string;
  field_type: string;
  parent_type: string;
  picklist_options?: Array<{ id: number; option: string }>;
}

interface ConditionLogicBuilderProps {
  title: string;
  groups: ConditionGroup[];
  onChange: (groups: ConditionGroup[]) => void;
  customFields: CustomFieldOption[];
  description?: string;
}

export function ConditionLogicBuilder({
  title,
  groups,
  onChange,
  customFields,
  description,
}: ConditionLogicBuilderProps) {
  const addGroup = () => {
    const newGroup: ConditionGroup = {
      id: `group-${Date.now()}-${Math.random()}`,
      conditions: [
        {
          id: `condition-${Date.now()}-${Math.random()}`,
          fieldId: 0,
          fieldName: '',
          operator: 'equals',
          value: '',
        },
      ],
    };
    
    onChange([...groups, newGroup]);
  };

  const updateGroup = (groupId: string, updatedGroup: ConditionGroup) => {
    onChange(groups.map(g => (g.id === groupId ? updatedGroup : g)));
  };

  const removeGroup = (groupId: string) => {
    onChange(groups.filter(g => g.id !== groupId));
  };

  const hasGroups = groups && groups.length > 0;

  return (
    <div className="space-y-4">
      {description && (
        <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
          {description}
        </p>
      )}

      {/* Groups */}
      {hasGroups ? (
        <div className="space-y-4">
          {groups.map((group, index) => (
            <ConditionGroupBuilder
              key={group.id}
              group={group}
              groupIndex={index}
              customFields={customFields}
              onChange={updatedGroup => updateGroup(group.id, updatedGroup)}
              onRemove={() => removeGroup(group.id)}
              showOrConnector={index > 0}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 border-2 border-dashed border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-lg">
          <Icon name="HiInformationCircle" className="w-8 h-8 mx-auto mb-2 text-xtnd-dark-300" />
          <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mb-3">
            No conditional logic configured. Click below to add your first condition group.
          </p>
        </div>
      )}

      {/* Add OR Group Button */}
      <Button
        type="button"
        variant="primary"
        size="md"
        onClick={addGroup}
        className="w-full"
        leftIcon={<Icon name="HiPlus" />}
      >
        Add {hasGroups ? 'OR' : ''} Group
      </Button>

      {/* Logic Preview */}
      {hasGroups && (
        <div className="mt-4 p-4 bg-xtnd-light/30 dark:bg-xtnd-dark/30 rounded-md border border-xtnd-blue/30">
          <div className="flex items-start gap-2">
            <Icon name="HiLightBulb" className="w-5 h-5 text-xtnd-blue mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-white mb-1">
                Logic Preview
              </p>
              <div className="text-xs text-xtnd-dark-600 dark:text-xtnd-dark-200 space-y-1">
                {groups.map((group, groupIndex) => (
                  <div key={group.id}>
                    {groupIndex > 0 && (
                      <span className="font-bold text-xtnd-dark dark:text-xtnd-white">OR </span>
                    )}
                    <span className="font-mono bg-white dark:bg-xtnd-dark-700 px-2 py-1 rounded">
                      (
                      {group.conditions.map((condition, condIndex) => (
                        <span key={condition.id}>
                          {condIndex > 0 && <span className="font-bold"> AND </span>}
                          {condition.fieldName || 'Field'} {condition.operator.replace(/_/g, ' ')}{' '}
                          {Array.isArray(condition.value)
                            ? `[${condition.value.join(', ')}]`
                            : condition.value === null
                              ? '(NULL)'
                              : condition.value || '""'}
                        </span>
                      ))}
                      )
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

